// apps/web/instrumentation-client.ts
import { initBotId } from "botid/client/core";
import posthog from "posthog-js";
import { initBrowserObservability } from "@atriam/observability/web";
import { setClientLogContext } from "@atriam/observability/web-context";

import { buildPostHogConfig } from "@/lib/analytics/posthog-config";
import { registerAcquisitionContext } from "@/lib/analytics/traffic-context";
import { env } from "@/lib/env";
import { stripRecapTokenEarly } from "@/lib/features/contractor-outreach/client/recap-token";
import { takeSigningFragmentEarly } from "@/lib/features/documents/client/signing-fragment-token";
import { PRODUCT, PRODUCT_DEF } from "@/lib/product";

// First, before PostHog or OpenTelemetry can record location.href: a
// fragment signing link (/sign/w9#token=…) hands its token to the page
// through this tab's sessionStorage and leaves the address bar token-less.
// PostHog's first pageview, its stored $initial_person_info and the
// document-load span then never hold it.
if (typeof window !== "undefined") {
  takeSigningFragmentEarly();
  stripRecapTokenEarly();
}

// BotID protects public share-page server actions that bots could abuse if
// they fuzz tokens (markInvoiceViewed inflates view counts; acceptEstimate /
// declineEstimate would commit accept/decline state on a guessed token).
// For a server action the path is the PAGE path that invokes it, not the
// action name; for a fetch to a route handler it is the API path itself —
// the W-9 signing page POSTs the signature to /api/documents/sign/[token]
// (a link with the token in its path) or /api/documents/signing-link/w9 (a
// link with the token after `#`), and both call checkBotId(). `*` matches
// across "/" (the client compiles it to `.*`, anchored), so the second entry
// leaves the link read, POST /api/documents/signing-link itself, unchallenged
// like the GET it replaces.
initBotId({
  protect: [
    { path: "/share/invoice/*", method: "POST" },
    { path: "/share/estimate/*", method: "POST" },
    { path: "/share/change-order/*", method: "POST" },
    { path: "/api/documents/sign/*", method: "POST" },
    { path: "/api/documents/signing-link/*", method: "POST" },
    // Certificate-of-insurance uploads (/sign/coi/<token>) call checkBotId().
    { path: "/api/documents/coi/*", method: "POST" },
  ],
});

// Browser OTel ships to a same-origin proxy under `/api/otlp/*`. The proxy
// forwards to SigNoz with the ingest token attached server-side, so:
//   1. The token is no longer in the browser bundle (was scrapable + replayable).
//   2. localhost dev works without CORS gymnastics (POSTs go to the same host).
// Server-side instrumentation talks to SigNoz directly via OTEL_EXPORTER_OTLP_ENDPOINT;
// it doesn't need the proxy.
//
// NOT `/api/_otlp`: App Router treats `_`-prefixed segments as private
// folders and excludes them from routing entirely, so the original proxy at
// that path never existed — browsers got 200 HTML back and the exporter
// silently dropped every batch (tests/unit/api/no-private-api-routes.test.ts
// locks this).
const PROXY_ENDPOINT = "/api/otlp";

// PostHog stays gated off localhost so `pnpm dev` / `pnpm dev:production`
// never pollute prod analytics funnels — that data only makes sense from
// real users. OTel is fine on localhost (proxy makes it cheap) and dev
// traffic in SigNoz is filterable by `deployment.environment.name`.
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

if (typeof window !== "undefined") {
  // Lazy-load post-hydration to avoid blocking FCP/LCP.
  // Token left empty — the proxy injects the real one server-side. Passing
  // a placeholder keeps `initBrowserObservability`'s required-arg shape
  // intact without exposing a real credential.
  const start = () =>
    initBrowserObservability({
      serviceName: "atriam-web-browser",
      serviceVersion: env.NEXT_PUBLIC_APP_VERSION,
      endpoint: PROXY_ENDPOINT,
      // Empty token = browser doesn't attach an Authorization header; the
      // proxy injects the real token server-side. `initBrowserObservability`
      // expects a string for type safety; the SDK gates the header on
      // non-empty.
      ingestToken: "",
      attributes: {
        product: PRODUCT,
        signup_product: PRODUCT_DEF.signupProduct,
      },
    });

  if ("requestIdleCallback" in window) {
    requestIdleCallback(start);
  } else {
    setTimeout(start, 2000);
  }
}

const posthogKey = env.NEXT_PUBLIC_POSTHOG_KEY;
if (typeof window !== "undefined" && posthogKey && !isLocalhost) {
  posthog.init(posthogKey, {
    ...buildPostHogConfig(window.location.pathname),
    loaded: registerAcquisitionContext,
  });
  posthog.register({
    product: PRODUCT,
    signup_product: PRODUCT_DEF.signupProduct,
    app_version: env.NEXT_PUBLIC_APP_VERSION,
    ...(process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID
      ? {
          vercel_deployment_id: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
        }
      : {}),
  });

  // Push the PostHog session id into the OTel client log context so every
  // browser log/span carries the join key back to a replay. The id is
  // assigned lazily on first event; subscribe to rotation so the context
  // updates with each new session window.
  const stampSession = () => {
    const sid = posthog.get_session_id();
    setClientLogContext({
      extra: {
        product: PRODUCT,
        signup_product: PRODUCT_DEF.signupProduct,
        ...(sid ? { posthog_session_id: sid } : {}),
      },
    });
  };
  stampSession();
  posthog.onSessionId(stampSession);
}

// Sentry's onRouterTransitionStart export is no longer needed. Browser OTel
// deliberately keeps document-load and fetch spans only; click-root spans
// were the dominant low-value ingest source and are intentionally disabled.
export {};
